import { globalizeSelectors } from '../Utils';
import PropTypes from 'prop-types';
import { fulfilled } from '../factories/ApiCall';
import { offline_mode } from '../../Constants';
import log from '../../api/Logger';
import type {
  OfflineAccessTimeRemaining,
  OfflineAction,
  OfflineInfo,
  OfflineState,
} from '../types/Offline';
/**
 * Just a simple flag to notify the UI that progress modal needs to be shown!
 */

/*
 * action types
 */

const types = {
  QT_GET_CLOUD_SERVER_ADDRESS: 'OFFLINE/QT_GET_CLOUD_SERVER_ADDRESS',
  QT_SET_ORDERS_SYNCED: 'OFFLINE/QT_SET_ORDERS_SYNCED',
  SET_ONLINE_AVAILABLE: 'OFFLINE/SET_ONLINE_AVAILABLE',
  SET_CLOUD_ADDRESS: 'OFFLINE/SET_CLOUD_ADDRESS',
  SET_OFFLINE_INFO: 'OFFLINE/SET_OFFLINE_INFO',
  START_OFFLINE_MODE: 'OFFLINE/START_OFFLINE_MODE',
  OFFLINE_MODE_STARTED: 'OFFLINE/OFFLINE_MODE_STARTED',
  STOP_OFFLINE_MODE: 'OFFLINE/STOP_OFFLINE_MODE',
  OFFLINE_MODE_STOPPED: 'OFFLINE/OFFLINE_MODE_STOPPED',
  SET_OFFLINE_MODE_AVAILABLE: 'OFFLINE/SET_OFFLINE_MODE_AVAILABLE',
  SET_FALL_BACKEND_RUNNING: 'OFFLINE/SET_FALL_BACKEND_RUNNING',
  RE_TRY_REQUEST: 'OFFLINE/RE_TRY_REQUEST',
  QT_FORCE_CHECK_FILES: 'OFFLINE/QT_FORCE_CHECK_FILES',
  QT_FORCE_CHECK_FILES_RUNNING: 'OFFLINE/QT_FORCE_CHECK_FILES_RUNNING',
  QT_FORCE_CHECK_FILES_FINISHED: 'OFFLINE/QT_FORCE_CHECK_FILES_FINISHED',
  OFFLINE_TIME_REMAINING: 'OFFLINE/OFFLINE_TIME_REMAINING',
  OFFLINE_REMOVE_FILES_AND_SETTINGS:
    'OFFLINE/OFFLINE_REMOVE_FILES_AND_SETTINGS',
} as const;

function payloadAs<Payload>(action: OfflineAction): Payload {
  return action.payload as Payload;
}

/*
 * action creators
 */

const actions = {
  setOnlineAvailable: (bool: boolean): OfflineAction<boolean> => ({
    type: types.SET_ONLINE_AVAILABLE,
    payload: bool,
  }),
  setOrdersSynced: (bool: boolean): OfflineAction<boolean> => ({
    type: types.QT_SET_ORDERS_SYNCED,
    payload: bool,
  }),
  setForceCheckRunning: (bool: boolean): OfflineAction<boolean> => ({
    type: types.QT_FORCE_CHECK_FILES_RUNNING,
    payload: bool,
  }),
  setOfflineAvailable: (bool: boolean): OfflineAction<boolean> => ({
    type: types.SET_OFFLINE_MODE_AVAILABLE,
    payload: bool,
  }),
  setCloudAddress: (address: string | null): OfflineAction<string | null> => ({
    type: types.SET_CLOUD_ADDRESS,
    payload: address,
  }),
  setOfflineInfo: (
    info: OfflineInfo | null
  ): OfflineAction<OfflineInfo | null> => ({
    type: types.SET_OFFLINE_INFO,
    payload: info,
  }),
  checkOfflineFiles: (): OfflineAction => ({
    type: types.QT_FORCE_CHECK_FILES,
  }),
  forceCheckFinished: (): OfflineAction => ({
    type: types.QT_FORCE_CHECK_FILES_FINISHED,
  }),
  startOfflineMode: (): OfflineAction => ({ type: types.START_OFFLINE_MODE }),
  stopOfflineMode: (): OfflineAction => ({ type: types.STOP_OFFLINE_MODE }),
  setFallBackendRunning: (bool: boolean): OfflineAction<boolean> => ({
    type: types.SET_FALL_BACKEND_RUNNING,
    payload: bool,
  }),
  reTryRequest: (): OfflineAction => ({
    type: types.RE_TRY_REQUEST,
  }),
  getCloudServerAddress: (): OfflineAction => ({
    type: types.QT_GET_CLOUD_SERVER_ADDRESS,
  }),
  getOfflineTimeRemaining: (): OfflineAction => ({
    type: types.OFFLINE_TIME_REMAINING,
  }),
  removeOfflineFilesAndSettings: (): OfflineAction => ({
    type: types.OFFLINE_REMOVE_FILES_AND_SETTINGS,
  }),
};
const mountPath = 'offline'; // mount point in global state, must match root reducer

/**
 * Handling login and log out site actions
 */

const initialState: OfflineState = {
  is_online_mode_available: true, // Notify user about the connection lost / recovered to online
  orders_synced: false,
  cloud_address: null, // Address of the cloud server (set by qt side)
  offline_info: null, // info about the files etc available on offline mode
  is_offline_mode_available: false, // Notify from QT side about the offline mode availability
  fall_backend_running: false, // State of fall backend
  show_modal: false, // Show offline modal
  force_check_running: false,
  time_remaining: null,
};

function reducer(
  state: OfflineState = initialState,
  action: OfflineAction
): OfflineState {
  switch (action.type) {
    case types.OFFLINE_MODE_STARTED: {
      log.info('OFFLINE_MODE_STARTED');
      return { ...state, show_modal: false };
    }
    case fulfilled(types.OFFLINE_TIME_REMAINING): {
      return {
        ...state,
        time_remaining: payloadAs<OfflineAccessTimeRemaining | null>(action),
      };
    }
    case types.OFFLINE_MODE_STOPPED: {
      log.info('OFFLINE_MODE_STOPPED');
      return { ...state, show_modal: false };
    }
    case types.SET_ONLINE_AVAILABLE: {
      const available = payloadAs<boolean>(action);
      // Show only if state changes
      let show = state.show_modal;
      if (!offline_mode === !available && !show) {
        log.info(`SET_ONLINE_AVAILABLE: ${available}`);
        show = true;
      }

      return {
        ...state,
        is_online_mode_available: available,
        show_modal: show,
      };
    }
    case types.RE_TRY_REQUEST: {
      return {
        ...state,
        show_modal: false,
      };
    }
    case types.SET_OFFLINE_MODE_AVAILABLE: {
      return {
        ...state,
        is_offline_mode_available: payloadAs<boolean>(action),
      };
    }
    case types.SET_CLOUD_ADDRESS: {
      return { ...state, cloud_address: payloadAs<string | null>(action) };
    }
    case types.SET_OFFLINE_INFO: {
      return { ...state, offline_info: payloadAs<OfflineInfo | null>(action) };
    }
    case types.QT_FORCE_CHECK_FILES_RUNNING: {
      return { ...state, force_check_running: payloadAs<boolean>(action) };
    }
    case types.SET_FALL_BACKEND_RUNNING: {
      return { ...state, fall_backend_running: payloadAs<boolean>(action) };
    }
    case types.QT_SET_ORDERS_SYNCED: {
      return { ...state, orders_synced: payloadAs<boolean>(action) };
    }

    default: {
      return state;
    }
  }
}

const propType = PropTypes.shape({
  is_online_mode_available: PropTypes.bool, // Notify user about the connection lost / recovered to online
  orders_synced: PropTypes.bool,
  cloud_address: PropTypes.string, // Address of the cloud server (set by qt side)
  is_offline_mode_available: PropTypes.bool, // Notify from QT side about the offline mode availability
  fall_backend_running: PropTypes.bool, // State of fall backend
  show_modal: PropTypes.bool, //
  offline_info: PropTypes.any,
  time_remaining: PropTypes.shape({
    days: PropTypes.number,
    hours: PropTypes.number,
    minutes: PropTypes.number,
    expiration_date: PropTypes.string,
  }),
});

// selectors

const localSelectors = {
  is_online_mode_available: (state: OfflineState): boolean =>
    state.is_online_mode_available,
  cloud_address: (state: OfflineState): OfflineState['cloud_address'] =>
    state.cloud_address,
  is_offline_mode_available: (state: OfflineState): boolean =>
    state.is_offline_mode_available,
  orders_synced: (state: OfflineState): boolean => state.orders_synced,
  time_remaining: (state: OfflineState): OfflineState['time_remaining'] =>
    state.time_remaining,
};

const selectors = globalizeSelectors(localSelectors, mountPath);

export {
  types as actionTypes,
  actions as default,
  selectors,
  reducer,
  propType,
};
